import React from 'react';
import { FileDown, Lock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import PDFDownloadLink from '../components/PDFDownloadLink';

const BRIEFINGS = [
  {
    variant: 'product',
    title: 'Product Overview',
    desc: 'How Forsyt sits on top of your CRM, calls, email and Slack to surface the next best action.',
    pill: 'PDF',
    color: 'blue',
  },
  {
    variant: 'security',
    title: 'Security & Trust Overview',
    desc: 'Architecture, certifications, sub-processors and the controls your security team will want to see.',
    pill: 'PDF',
    color: 'emerald',
  },
  {
    variant: 'metricstream',
    title: 'Forsyt for MetricStream',
    desc: 'Account-specific briefing on how Forsyt fits the MetricStream revenue motion.',
    pill: 'PDF',
    color: 'amber',
  },
  {
    variant: 'epiplex',
    title: 'Forsyt for Epiplex.ai',
    desc: 'Pipeline-visibility briefing tailored to Epiplex.ai, with the deal-state framing.',
    pill: 'PDF',
    color: 'cyan',
  },
  {
    variant: 'moengage',
    title: 'Forsyt for MoEngage',
    desc: 'CS-side briefing for MoEngage: silent churn, GainSight CTAs, sales-to-IM-to-CS handover.',
    pill: 'PDF',
    color: 'violet',
  },
  {
    variant: 'goaudit',
    title: 'Forsyt for GoAudit',
    desc: 'Compliance-cautious briefing for the GoAudit CS team, with GDPR posture and phased deployment.',
    pill: 'PDF',
    color: 'teal',
  },
  {
    variant: 'mci',
    title: 'Forsyt for MCI',
    desc: 'Sales-side cadence briefing for MCI, focused on follow-up rhythm and the silent middle of the funnel.',
    pill: 'PDF',
    color: 'orange',
  },
];

const COLOR_MAP = {
  blue: 'bg-blue-50 text-blue-700 border-blue-200 group-hover:bg-blue-100',
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200 group-hover:bg-emerald-100',
  amber: 'bg-amber-50 text-amber-700 border-amber-200 group-hover:bg-amber-100',
  cyan: 'bg-cyan-50 text-cyan-700 border-cyan-200 group-hover:bg-cyan-100',
  violet: 'bg-violet-50 text-violet-700 border-violet-200 group-hover:bg-violet-100',
  teal: 'bg-teal-50 text-teal-700 border-teal-200 group-hover:bg-teal-100',
  orange: 'bg-orange-50 text-orange-700 border-orange-200 group-hover:bg-orange-100',
};

const BriefingsPage = () => (
  <div className="min-h-screen bg-white">
    <SEO
      title="Briefings, Forsyt"
      description="Account-specific and product briefings from Forsyt. Confidential PDFs prepared for revenue and CS leadership teams."
      path="/briefings"
    />
    <Header />
    <main className="pt-20 pb-24">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-blue-600 bg-blue-50 border border-blue-100 px-3 py-1 rounded-full mb-5">
            <Lock className="w-3.5 h-3.5" />
            <span>Confidential briefings</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.04]">
            Briefings library.
          </h1>
          <p className="mt-5 text-xl text-slate-600 leading-relaxed max-w-2xl">
            Forsyt prepares short, account-specific briefings for the leadership teams we are in conversation with.
            Each one maps directly to the working session it followed. Shared here for your reference, kept off the
            main navigation.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {BRIEFINGS.map((b) => (
            <PDFDownloadLink
              key={b.variant}
              variant={b.variant}
              source="briefings-page"
              data-testid={`briefing-card-${b.variant}`}
              className="group flex flex-col p-6 rounded-2xl border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all bg-white"
            >
              <div className="flex items-start justify-between mb-3">
                <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border transition-colors ${COLOR_MAP[b.color]}`}>
                  <FileDown className="w-3 h-3" />
                  {b.pill}
                </span>
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">{b.title}</h2>
              <p className="text-sm text-slate-600 leading-relaxed">{b.desc}</p>
              <div className="mt-4 text-xs font-semibold text-slate-500 group-hover:text-blue-600 transition-colors">
                Download PDF →
              </div>
            </PDFDownloadLink>
          ))}
        </div>

        <div className="mt-14 p-6 bg-slate-50 border border-slate-200 rounded-2xl text-sm text-slate-600 leading-relaxed">
          <strong className="text-slate-900">Note:</strong> Account-specific briefings are prepared for a named
          leadership team and are not under embargo, but should be treated as confidential. If you need a briefing
          tailored to your business, please reach out to <a href="mailto:udit@forsyt.com" className="text-blue-600 font-semibold hover:underline">udit@forsyt.com</a>.
        </div>
      </div>
    </main>
    <Footer />
  </div>
);

export default BriefingsPage;
